import { useCallback, useRef } from 'react';
import { Search, FolderPlus, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useFolderPicker } from './useFolderPicker';
import { FolderPickerBreadcrumbs } from './FolderPickerBreadcrumbs';
import { FolderPickerList } from './FolderPickerList';
import { FolderPickerSkeleton } from './FolderPickerSkeleton';
import type { FolderDestination } from './types';

interface FolderPickerProps {
  useCase: 'move' | 'copy';
  workspaceName: string;
  selectedItems?: { id: string; itemtype: string }[];
  currentFolderId?: string;
  onSelectDestination: (dest: FolderDestination | undefined) => void;
  updateIsQuerying?: (querying: boolean) => void;
}

export function FolderPicker({
  useCase,
  workspaceName,
  selectedItems,
  currentFolderId,
  onSelectDestination,
  updateIsQuerying,
}: FolderPickerProps) {
  const picker = useFolderPicker({
    useCase,
    selectedItems,
    currentFolderId,
    onSelectDestination,
    updateIsQuerying,
  });

  const listRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const count = picker.filteredItems.length;
      if (count === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = picker.focusedIndex < count - 1 ? picker.focusedIndex + 1 : 0;
          picker.setFocusedIndex(next);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = picker.focusedIndex > 0 ? picker.focusedIndex - 1 : count - 1;
          picker.setFocusedIndex(prev);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (picker.focusedIndex >= 0 && picker.focusedIndex < count) {
            picker.selectItem(picker.filteredItems[picker.focusedIndex]);
          }
          break;
        }
        case 'Backspace': {
          if (picker.searchQuery === '') {
            e.preventDefault();
            picker.navigateToRoot();
          }
          break;
        }
      }
    },
    [picker],
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      {/* Breadcrumbs */}
      <FolderPickerBreadcrumbs
        currentFolder={picker.currentFolder}
        workspaceName={workspaceName}
        onNavigateRoot={picker.navigateToRoot}
        onNavigateFolder={picker.navigateToFolder}
      />

      {/* Toolbar: search + new folder */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={picker.searchQuery}
            onChange={(e) => picker.setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search folders..."
            className="h-8 w-full rounded-md border border-border bg-secondary pl-8 pr-3 text-sm text-accent-foreground outline-none placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-ring"
          />
        </div>
        <button
          type="button"
          onClick={() => picker.setIsCreatingFolder(true)}
          disabled={picker.isCreatingFolder || picker.status !== 'success'}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors',
            'text-primary-hover hover:bg-primary-muted disabled:opacity-40 disabled:hover:bg-transparent',
          )}
          title="New folder"
        >
          <FolderPlus className="h-4 w-4" />
          <span className="hidden sm:inline">New folder</span>
        </button>
      </div>

      {/* Content */}
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto"
        onKeyDown={handleKeyDown}
      >
        {picker.status === 'loading' && <FolderPickerSkeleton />}

        {picker.status === 'error' && (
          <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
            <p className="text-sm">Failed to load folders</p>
            <button
              type="button"
              onClick={picker.retry}
              className="flex items-center gap-1.5 rounded-md bg-secondary px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        )}

        {picker.status === 'success' && (
          <FolderPickerList
            items={picker.filteredItems}
            selectedItem={picker.selectedItem}
            focusedIndex={picker.focusedIndex}
            currentFolderId={currentFolderId}
            selectedItems={selectedItems}
            useCase={useCase}
            searchQuery={picker.searchQuery}
            isCreatingFolder={picker.isCreatingFolder}
            onSelect={picker.selectItem}
            onNavigate={picker.navigateToFolder}
            onCreateFolder={picker.createFolder}
            onCancelCreate={() => picker.setIsCreatingFolder(false)}
          />
        )}
      </div>
    </div>
  );
}
